import { GetCallMetadataRequest } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import {
  ApplyCallRecordTagsRequest,
  GetHydratedCallRecordResponse,
  PhoneRecordsAPI,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { CallDirection } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { EmptyPerson, MockPersons } from '@frontend/api-person';
import { DemoIdUtils, DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockAPIRouter } from '@frontend/fetch';
import { genUUIDV4 } from '@frontend/string';
import { CallRecord } from '../../types';
import { BaseMockRouterContext } from './types';

export interface CallIntelInsightPanelMockRouterContext extends BaseMockRouterContext {
  mockPersonsInstance: MockPersons;
}

export const CallIntelInsightPanelMockRouter: FeatureMockRouter<CallIntelInsightPanelMockRouterContext> = {
  addMockRouterEndpoints: (router, { mockPersonsInstance }) => {
    router.schemaEndpoint(PhoneRecordsAPI.GetHydratedCallRecord, async (request, _, next) => {
      const { searchParams } = new URL(request.url);
      const channelId = searchParams.get('channelId') ?? '';
      const isDemo = searchParams.get('isDemo') ?? '';

      if (!isDemo) {
        next();
        return null;
      }

      const locationId = searchParams.get('locationId') ?? '';
      const personId = searchParams.get('demoMetadata.personId') ?? '';
      const callRecordStringified = searchParams.get('demoMetadata.callRecord') ?? undefined;
      const callRecord: CallRecord | undefined = callRecordStringified ? JSON.parse(callRecordStringified) : undefined;

      const personData = personId ? mockPersonsInstance.getPersonV3({ personId }) : EmptyPerson;

      return MockAPIRouter.json({
        record: {
          channelId,
          locationId,
          person: personData,
          callIntelInsights: callRecord,
          callerNumber: callRecord?.phoneNumber?.nationalNumber ?? '',
          dialedNumber: callRecord?.phoneNumber?.nationalNumber ?? '',
          direction: callRecord?.direction === CallDirection.DIRECTION_OUTBOUND ? 'outbound' : 'inbound',
          tagIds: callRecord?.tagIds || [],
        },
      } as GetHydratedCallRecordResponse);
    });
  },

  getRequestInterceptors: ({ interceptorIdPrefix, vertical, hasVoiceAiAccess }) => {
    return [
      {
        id: `${interceptorIdPrefix}-call-metadata`,
        interceptor: async (request) => {
          const { origin, pathname } = new URL(request.url);
          const callMetadataPathRegex = /^\/call-intelligence\/v2\/call\/([a-z0-9-]+)\/metadata$/;

          if (request.method === 'POST' && callMetadataPathRegex.test(pathname)) {
            const channelId = pathname.match(callMetadataPathRegex)?.[1] ?? '';
            const randomUUID = genUUIDV4();
            const requestBody: GetCallMetadataRequest = await request.clone().json();

            if (!requestBody.isDemo && !channelId.startsWith('demo')) {
              return request;
            }

            const { callRecord, isFromContactPanel, ...demoMetadata } =
              requestBody.demoMetadata as GetCallMetadataRequest['demoMetadata'] & {
                callRecord: HydratedCallRecord;
                isFromContactPanel?: boolean;
              };

            return new Request(
              `${origin}/call-intelligence/v2/call/${channelId.startsWith('demo') ? randomUUID : channelId}/metadata`,
              {
                method: request.method,
                headers: request.headers,
                body: JSON.stringify({
                  ...requestBody,
                  id: randomUUID,
                  isDemo: true,
                  demoMetadata: {
                    ...demoMetadata,
                    firstName: demoMetadata.firstName || callRecord.person?.firstName || 'John',
                    lastName: demoMetadata.lastName || callRecord.person?.lastName || 'Doe',
                    gender: demoMetadata.gender || callRecord.person?.gender || 'MALE',
                    vertical,
                    callId: channelId,
                    features: {
                      enableCallIntelligence: true,
                      enableVoiceAi: hasVoiceAiAccess,
                    },
                    ...(isFromContactPanel && { enableDemoCallPool: true }),
                  },
                } as GetCallMetadataRequest),
              }
            );
          }
          return request;
        },
      },
      {
        id: `${interceptorIdPrefix}-edit-ai-output`,
        interceptor: async (request) => {
          const { pathname } = new URL(request.url);
          const editAIOutputAPIPath = /^\/call-intelligence\/v2\/call\/([a-z0-9-]+)$/;
          if (request.method === 'PUT' && editAIOutputAPIPath.test(pathname)) {
            const requestBody: GetCallMetadataRequest = await request.clone().json();
            requestBody.isDemo = true;
            requestBody.demoMetadata = {
              ...requestBody.demoMetadata,
              vertical,
              features: {
                enableCallIntelligence: true,
                enableVoiceAi: hasVoiceAiAccess,
              },
            };

            return new Request(request, {
              body: JSON.stringify(requestBody),
            });
          }
          return request;
        },
      },
      {
        id: `${interceptorIdPrefix}-tag-call`,
        interceptor: async (request) => {
          const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
            schemaMethod: PhoneRecordsAPI.ApplyCallRecordTags,
            requestUrl: request.url,
            method: request.method,
          });
          if (isMatching) {
            const requestBody: ApplyCallRecordTagsRequest = await request.clone().json();

            if (DemoIdUtils.checkIsDemoId(requestBody.channelId) || requestBody.isDemo) {
              requestBody.isDemo = true;
              requestBody.channelId = DemoIdUtils.removeDemoDataFromId(requestBody.channelId);
              if (requestBody.startTime === '') {
                delete requestBody.startTime;
              }

              return new Request(request, {
                body: JSON.stringify(requestBody),
              });
            }
          }

          return request;
        },
      },
      {
        id: `${interceptorIdPrefix}-remove-tags-from-call`,
        interceptor: async (request) => {
          const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
            schemaMethod: PhoneRecordsAPI.RemoveCallRecordTags,
            requestUrl: request.url,
            method: request.method,
          });
          if (isMatching) {
            const { searchParams, pathname, origin } = new URL(request.url);
            const channelId = searchParams.get('channelId');
            if ((channelId && DemoIdUtils.checkIsDemoId(channelId)) || searchParams.get('isDemo')) {
              const updatedParams = new URLSearchParams(searchParams);
              updatedParams.set('isDemo', 'true');
              // Remove empty start time
              if (searchParams.get('startTime') === '') {
                updatedParams.delete('startTime');
              }
              return new Request(`${origin}${pathname}?${updatedParams.toString()}`, request);
            }
          }

          return request;
        },
      },
    ];
  },
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries(['hydrated-call-record']);
    // Other query interceptor have 'demo' key checked in request so they do not need to be invalidated
  },
};
